import React, { FC, MouseEvent, useContext, useMemo, useState } from 'react';

import { PopoverPosition } from '@mui/material';
import { RenderElementProps } from 'slate-react';

import AampeTooltip from '../../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';
// constants
import ICONS from '../../../../constants/icons';

//context
import { EditorContext } from '../../../../context/EditorContext';

// hooks
import useCMSPlaceholderQuery from '../../../../hooks/cms-placeholders/useCMSPlaceholderQuery';
import useOutsideClickDetector from '../../../../hooks/useOutsideClickDetector';

// models
import { CMSPlaceholderModel } from '../../../../models/cms-placeholder.model';
import { CMSPlaceholderElement } from '../../../../models/editor.model';

// components
import CMSPlaceholderDropdown from './CMSPlaceholderDropdown';
import ZeroWidthSpace from './ZeroWidthSpace';

type CMSPlaceholderElementProps = RenderElementProps & {
    element: CMSPlaceholderElement;
    selectedElement?: { [key: string]: any };
    editCMSPlaceholder: (id: string, cmsPlaceholderId: string) => void;
};

const CMSPlaceholderElementComponent: FC<
    React.PropsWithChildren<CMSPlaceholderElementProps>
> = ({
    children,
    element,
    selectedElement,
    attributes,
    editCMSPlaceholder,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);
    const { data } = useCMSPlaceholderQuery();

    const [placeholderAnchorPosition, setPlaceholderAnchorPosition] = useState<
        PopoverPosition | undefined
    >();

    const currentPlaceholderItem = useMemo<
        CMSPlaceholderModel | undefined
    >(() => {
        return (data?.messageable ?? []).find(
            (d) => d.id === element.cmsPlaceholderId,
        );
    }, [data, element]);

    const stringBlock = useMemo<string>(() => {
        let selected = 'Select Placeholder';
        if (element.cmsPlaceholderId) {
            if (currentPlaceholderItem) {
                selected = currentPlaceholderItem.name;
            }
        }
        return selected;
    }, [element, currentPlaceholderItem]);

    const sampleValue = useMemo<string>(() => {
        let value = '';
        if (element.cmsPlaceholderId) {
            if (currentPlaceholderItem && currentPlaceholderItem.sample_value) {
                value = currentPlaceholderItem.sample_value;
            }
        }
        return value || stringBlock;
    }, [currentPlaceholderItem, element, stringBlock]);

    const placeholderRef = useOutsideClickDetector(() =>
        setPlaceholderAnchorPosition(undefined),
    );

    const handleClickPlaceholder = (e: MouseEvent<HTMLSpanElement>) => {
        setPlaceholderAnchorPosition({
            top: (e.clientY ?? 0) + 10,
            left: e.clientX ?? 0,
        });
    };

    const toolTipTitle = useMemo<string>(() => {
        return `${formula?.cms_type}`;
    }, [formula]);

    return (
        <Container {...attributes}>
            <AampeTooltip arrow title={toolTipTitle} placement="top">
                <div ref={placeholderRef}>
                    <PlaceholderComponent onClick={handleClickPlaceholder}>
                        <StyledCMSIcon />
                        {sampleValue}
                    </PlaceholderComponent>

                    <ZeroWidthSpace />
                    {children}
                    <ZeroWidthSpace />
                </div>
            </AampeTooltip>
            {placeholderAnchorPosition && (
                <CMSPlaceholderDropdown
                    anchorPosition={placeholderAnchorPosition}
                    handleClick={(cms) =>
                        editCMSPlaceholder(selectedElement!.elementId, cms)
                    }
                />
            )}
        </Container>
    );
};

export default CMSPlaceholderElementComponent;

const Container = styled.span({
    userSelect: 'none',
    outline: 'none',
    display: 'inline-block',
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.interactiveWG01,
    padding: aampeTheme.spacing4(0, 1),
    color: aampeTheme.colors.neutral09,
    lineHeight: '24px',
    fontSize: aampeTheme.size.font.default,
    fontWeight: 400,
    minWidth: '30px',
    border: `1px solid ${aampeTheme.colors.solidA02}`,
});

const StyledCMSIcon = styled(ICONS.cmsPlaceHolder)({
    lineHeight: '20px',
    verticalAlign: 'middle',
    margin: aampeTheme.spacing4(0, 1, 0, 0),
    width: '16px',
    height: '16px',
    color: 'inherit',
});

const PlaceholderComponent = styled.span({
    userSelect: 'none',
});
